/**
 * shadcn/Tailwind replacement for MarkdownViewerWithSourceTabs.
 * Shows rendered markdown by default with a clean toolbar to toggle source view.
 */

import React, { useMemo, useRef, useState } from 'react';
import { Eye, Code2 } from 'lucide-react';
import { ShadcnMarkdownPreview } from './ShadcnMarkdownPreview';
import { ScimAceEditor } from './ScimAceEditor';
import { cn } from '../../lib/utils';

export interface ShadcnContentViewerProps {
    content: string;
    height?: string;
    emptyMessage?: string;
    defaultTab?: 'preview' | 'source';
    onContentLinkClick?: (entityId: string, displayName: string) => void;
}

export const ShadcnContentViewer: React.FC<ShadcnContentViewerProps> = ({
    content,
    height = '400px',
    emptyMessage = 'No content to display',
    defaultTab = 'preview',
    onContentLinkClick,
}) => {
    const [tab, setTab] = useState<'preview' | 'source'>(defaultTab);
    const previewScrollRef = useRef<HTMLDivElement | null>(null);
    const normalized = useMemo(() => (content ?? '').toString(), [content]);

    return (
        <div className="rounded-lg border border-border bg-card overflow-hidden">
            {/* Tab bar */}
            <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border bg-muted/30">
                <button
                    onClick={() => setTab('preview')}
                    className={cn(
                        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                        tab === 'preview'
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground hover:bg-background/50',
                    )}
                >
                    <Eye size={14} />
                    Preview
                </button>
                <button
                    onClick={() => setTab('source')}
                    className={cn(
                        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                        tab === 'source'
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground hover:bg-background/50',
                    )}
                >
                    <Code2 size={14} />
                    Source
                </button>
            </div>

            {/* Content */}
            {tab === 'preview' ? (
                <div
                    ref={previewScrollRef}
                    className="overflow-auto p-4"
                    style={{ height }}
                >
                    <ShadcnMarkdownPreview
                        content={normalized}
                        emptyMessage={emptyMessage}
                        scrollContainerRef={previewScrollRef}
                        debugAnchors
                        onContentLinkClick={onContentLinkClick}
                        className="border-0 p-0"
                    />
                </div>
            ) : (
                <ScimAceEditor
                    mode="markdown"
                    value={normalized}
                    readOnly
                    name="markdown-source-viewer"
                    height={height}
                    fontSize={14}
                    showPrintMargin={false}
                    highlightActiveLine={false}
                    setOptions={{
                        showLineNumbers: true,
                        tabSize: 2,
                        useWorker: false,
                    }}
                />
            )}
        </div>
    );
};
